import {CabinetMesh} from 'components/customer/RoomPlanner/types';

/** Same defaults as renderCabinetGroup / a standard 550mm wall cabinet. */
export const DEFAULT_UPPER_CABINET_HEIGHT_MM = 550;
const DEFAULT_UPPER_LAYOUT = {
    toeKickHeightDefault: 150,
    tallHeight: 1950,
    sitOnFloor: 51,
};

/**
 * World Y of a default wall cabinet's origin (mesh bottom). Upper placeholders
 * hang here so the gray floor-distance matches a normal upper (1550mm).
 * @param {number} wallHeightScene - Wall height in scene units
 * @return {number} World Y for the underside / origin
 */
export const getDefaultUpperCabinetOriginY = (
    wallHeightScene: number
): number =>
    calculatePositionY(
        {
            userData: {
                isUpperProduct: true,
                values: {cabinet_height: DEFAULT_UPPER_CABINET_HEIGHT_MM},
            },
        } as CabinetMesh,
        wallHeightScene,
        DEFAULT_UPPER_LAYOUT
    );

/**
 * Calculates the Y-axis position of a cabinet mesh inside the room planner scene.
 *
 * The position is derived from the cabinet type (upper, floor, or standard),
 * wall height, toe-kick configuration, and predefined layout constants.
 *
 * @param {CabinetMesh} product - The cabinet mesh whose Y position is being calculated.
 * @param {number} currentWallHeight - Total height of the wall in the scene.
 * @param {Object} DEFAULT_POSITION_Y - Predefined vertical layout constants.
 * @param {number} DEFAULT_POSITION_Y.toeKickHeightDefault - Default toe-kick height.
 * @param {number} DEFAULT_POSITION_Y.tallHeight - Default tall cabinet height.
 * @param {number} DEFAULT_POSITION_Y.sitOnFloor - Y offset for cabinets sitting on the floor.
 *
 * @return {number} The calculated Y-axis position for the cabinet.
 */
export function calculatePositionY(
    product: CabinetMesh,
    currentWallHeight: number,
    DEFAULT_POSITION_Y: {
        toeKickHeightDefault: number;
        tallHeight: number;
        sitOnFloor: number;
    }
): number {
    const {userData} = product;

    const isUpperProduct = userData.isUpperProduct;
    const sitOnFloor = userData.sitOnFloor;

    const toeKickHeight = Number(userData.toeKickHeight) || 81;
    const extendsToFloor = Boolean(userData.values?.cabinet_extend);

    let positionY: number;

    if (isUpperProduct) {
        const upperCabinetHeight =
            Number(userData.values?.cabinet_height) || 550;

        const isBulkheadComponent = userData.isBulkheadComponent;

        const upperCabinetScale =
            (DEFAULT_POSITION_Y.toeKickHeightDefault +
                DEFAULT_POSITION_Y.tallHeight -
                (isBulkheadComponent ? 0 : upperCabinetHeight)) *
            (451 / 2000);

        const upperCabinetPositionY =
            upperCabinetScale + (451 - upperCabinetScale) * 0.113;

        positionY = -(currentWallHeight / 2) + upperCabinetPositionY;
    } else if (sitOnFloor || extendsToFloor) {
        positionY = -(currentWallHeight / 2) + DEFAULT_POSITION_Y.sitOnFloor;
    } else {
        positionY = -(currentWallHeight / 2) + toeKickHeight;
    }

    return positionY;
}
